import { DocumentationTabTypes } from '../../../../types/enums/DocumentationTabTypes';
import { FieldsTable } from '../FieldsTable';
import { useDocumentation } from '../../useDocumentation';
import { getCardListFromConfigurationItem, getCardListFromParsedRecordTypes } from '../utils';
import { useSelector } from 'react-redux';
import { hasFieldProperties } from '../../../parser/ConfigurationItemTypes';
import {
  selectDependenciesConfigurationItem,
  selectSingleObject,
  selectSingleObjectActiveTab,
} from '../../../../reducers/documentation/documentationReducer';
import { useDependencies } from '../../dependencies/hooks/useDependencies';
import { CardsList } from './CardsList';
import { selectCrmOrgHasManagePackage } from '../../../pages/environments/environmentsReducer';
import { MenuElementsWithBadge } from './MenuElementsWithBadge';
import { SalesforceItem } from '../../../parser/ParserTypes';
import { SelectedObjectListElements } from '../../types';
import { MetadataCards } from './MetadataCards';

interface SelectedObjectCardsProps {
  crmOrgId: string;
  items: SalesforceItem[] | SelectedObjectListElements;
}

export const SelectedObjectCards = ({ crmOrgId, items }: SelectedObjectCardsProps) => {
  const objectTypeName = useSelector(selectSingleObject);
  const activeTab = useSelector(selectSingleObjectActiveTab);
  const isManagePackageInstalled = useSelector(selectCrmOrgHasManagePackage(crmOrgId));
  const { onFieldClick, onTabChange } = useDocumentation();
  const { handleSetConfigurationItem } = useDependencies(crmOrgId);
  const selectedDependency = useSelector(selectDependenciesConfigurationItem);
  const firstHistoryItem = selectedDependency?.history?.[0];

  if (!objectTypeName) {
    return <MetadataCards crmOrgId={crmOrgId} list={items as SalesforceItem[]} />;
  }

  if (!objectTypeName || !activeTab) return null;

  const itemsAsConfigurationItems = items as SalesforceItem[];

  const renderActiveTab = () => {
    switch (activeTab) {
      case DocumentationTabTypes.CARDS_LIST:
        return (
          <MenuElementsWithBadge
            elements={items as SelectedObjectListElements}
            onClick={onTabChange}
          />
        );

      case DocumentationTabTypes.FIELDS:
        return (
          <FieldsTable
            crmOrgId={crmOrgId}
            fields={itemsAsConfigurationItems.filter(hasFieldProperties)}
            isManagePackageInstalled={!!isManagePackageInstalled}
            objectName={objectTypeName?.objectType}
            onFieldClick={onFieldClick}
            firstHistoryItem={firstHistoryItem}
          />
        );

      case DocumentationTabTypes.RECORD_TYPES:
        return (
          <CardsList
            list={getCardListFromParsedRecordTypes(
              itemsAsConfigurationItems,
              objectTypeName.objectType,
            )}
            firstHistoryItem={firstHistoryItem}
            handleSetConfigurationItem={handleSetConfigurationItem}
          />
        );

      case DocumentationTabTypes.RELATED_OBJECTS:
        return (
          <CardsList
            list={getCardListFromConfigurationItem(
              itemsAsConfigurationItems,
              objectTypeName.objectType,
            )}
            firstHistoryItem={firstHistoryItem}
            handleSetConfigurationItem={handleSetConfigurationItem}
            withDetailedContentForRelatedObjects
            withApiNameOnHover
          />
        );

      default:
        return (
          <CardsList
            list={getCardListFromConfigurationItem(
              itemsAsConfigurationItems,
              objectTypeName.objectType,
            )}
            firstHistoryItem={firstHistoryItem}
            handleSetConfigurationItem={handleSetConfigurationItem}
          />
        );
    }
  };

  return renderActiveTab();
};
